<template>
  <div class="seo-keyword-page" v-loading="pageLoading">
    <div class="seo-keyword-page__header">
      <div>
        <div class="seo-keyword-page__title">{{ pageTitle }}</div>
        <div class="seo-keyword-page__desc">{{ pageDesc }}</div>
      </div>
      <div class="seo-keyword-page__actions">
        <el-button
          v-if="isKeywordDetail"
          size="small"
          icon="el-icon-arrow-left"
          @click="backToSiteList"
        >
          返回关键词管理
        </el-button>
        <el-button
          v-if="$hasPermission('seo.keyword', 'create')"
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="onNew"
        >
          新增关键词
        </el-button>
        <el-button
          v-if="$hasPermission('seo.keyword', 'create')"
          size="small"
          icon="el-icon-upload2"
          :loading="importLoading"
          @click="onImportClick"
        >
          CSV导入
        </el-button>
        <el-button
          size="small"
          icon="el-icon-download"
          @click="onDownloadTemplate"
        >
          下载模板
        </el-button>
        <input
          ref="importFileRefs"
          type="file"
          accept=".csv,text/csv"
          class="seo-keyword-page__hidden-input"
          @change="onImportFileChange"
        />
      </div>
    </div>

    <div v-if="isKeywordDetail" class="seo-keyword-page__site-strip">
      <div>
        <div class="seo-keyword-page__site-title">
          {{ currentSite.site_name || "站点关键词" }}
        </div>
        <div class="seo-keyword-page__site-url">
          {{ currentSite.site_url || "-" }}
        </div>
      </div>
      <div class="seo-keyword-page__site-meta">
        <div class="seo-keyword-page__site-metric">
          <span>承诺数</span>
          <strong>{{ currentSite.committed_keyword_count || 0 }}</strong>
        </div>
        <div class="seo-keyword-page__site-metric">
          <span>达标</span>
          <strong>{{ currentSite.qualified_count || 0 }}</strong>
        </div>
        <div
          class="seo-keyword-page__site-metric seo-keyword-page__site-metric--rate"
        >
          <span>达成率</span>
          <strong>{{ currentSite.achievement_rate || "-" }}</strong>
        </div>
      </div>
    </div>

    <div class="seo-keyword-page__panel seo-keyword-page__filter-panel">
      <div v-if="!isKeywordDetail" class="seo-keyword-page__filters">
        <el-select
          v-model="siteParams.site_id"
          filterable
          clearable
          size="small"
          placeholder="全部站点"
          class="seo-keyword-page__filter-site"
        >
          <el-option
            v-for="item in siteOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
        <el-input
          v-model="siteParams.site_url"
          clearable
          size="small"
          placeholder="请输入站点URL"
          class="seo-keyword-page__filter-input"
          @keyup.enter.native="getSiteSummary(1)"
        ></el-input>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-search"
          @click="getSiteSummary(1)"
        >
          查询
        </el-button>
        <el-button size="small" @click="resetSiteFilters">重置</el-button>
      </div>

      <div v-else class="seo-keyword-page__filters">
        <el-input
          v-model="keywordParams.keyword"
          clearable
          size="small"
          placeholder="关键词"
          class="seo-keyword-page__filter-input"
          @keyup.enter.native="getList(1)"
        ></el-input>
        <el-input
          v-model="keywordParams.target_domain"
          clearable
          size="small"
          placeholder="目标域名"
          class="seo-keyword-page__filter-input"
          @keyup.enter.native="getList(1)"
        ></el-input>
        <el-select
          v-model="keywordParams.keyword_type"
          clearable
          size="small"
          placeholder="关键词类型"
          class="seo-keyword-page__filter-small"
        >
          <el-option
            v-for="item in keywordTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
        <el-select
          v-model="keywordParams.include_in_rank_check"
          clearable
          size="small"
          placeholder="参与排名"
          class="seo-keyword-page__filter-small"
        >
          <el-option label="参与" value="1"></el-option>
          <el-option label="不参与" value="0"></el-option>
        </el-select>
        <el-select
          v-model="keywordParams.qualified_status"
          clearable
          size="small"
          placeholder="达标"
          class="seo-keyword-page__filter-small"
        >
          <el-option label="达标" value="qualified"></el-option>
          <el-option label="未达标" value="unqualified"></el-option>
        </el-select>
        <el-select
          v-model="keywordParams.status"
          clearable
          size="small"
          placeholder="状态"
          class="seo-keyword-page__filter-small"
        >
          <el-option label="正常" value="1"></el-option>
          <el-option label="停用" value="0"></el-option>
        </el-select>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-search"
          @click="getList(1)"
        >
          查询
        </el-button>
        <el-button size="small" @click="resetKeywordFilters">重置</el-button>
      </div>
    </div>

    <div v-if="!isKeywordDetail" class="seo-keyword-page__panel">
      <div class="seo-keyword-page__panel-head">
        <div>
          <div class="seo-keyword-page__panel-title">站点关键词列表</div>
          <div class="seo-keyword-page__panel-note">
            保留现有站点统计字段，进入站点后查看全部关键词
          </div>
        </div>
      </div>
      <div class="data-table-scroll" style="--table-min-width: 1670px">
        <el-table :data="sitePager.list" empty-text="暂无站点关键词数据">
          <el-table-column
            prop="site_id"
            label="站点ID"
            width="80"
          ></el-table-column>
          <el-table-column
            prop="site_name"
            label="站点"
            width="150"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column
            prop="site_url"
            label="站点URL"
            min-width="200"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column
            prop="committed_keyword_count"
            label="承诺数"
            width="90"
          ></el-table-column>
          <el-table-column
            prop="keyword_count"
            label="关键词总数"
            width="110"
          ></el-table-column>
          <el-table-column
            prop="core_count"
            label="主词"
            width="80"
          ></el-table-column>
          <el-table-column
            prop="long_tail_count"
            label="长尾词"
            width="90"
          ></el-table-column>
          <el-table-column
            prop="brand_count"
            label="品牌词"
            width="90"
          ></el-table-column>
          <el-table-column
            prop="competitor_count"
            label="竞品词"
            width="90"
          ></el-table-column>
          <el-table-column
            prop="rank_check_count"
            label="参与排名"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="found_count"
            label="已找到"
            width="90"
          ></el-table-column>
          <el-table-column
            prop="qualified_count"
            label="达标数"
            width="90"
          ></el-table-column>
          <el-table-column label="承诺类型" width="100">
            <template v-slot="scope">
              {{ committedRankLimitText(scope.row.committed_rank_limit) }}
            </template>
          </el-table-column>
          <el-table-column label="达成率" width="100">
            <template v-slot="scope">
              <span>{{ scope.row.achievement_rate || "-" }}</span>
            </template>
          </el-table-column>
          <el-table-column fixed="right" label="操作" width="110">
            <template v-slot="scope">
              <el-button type="text" @click="openSiteKeywords(scope.row)">
                查看关键词
              </el-button>
              <el-button
                v-if="$hasPermission('seo.keyword', 'edit')"
                type="text"
                :loading="syncSiteLoadingId === scope.row.site_id"
                @click="onSyncSiteRank(scope.row)"
              >
                同步排名
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="seo-keyword-page__pagination">
        <pagination
          v-model="sitePager"
          :pageSizes="[10, 20, 30]"
          @change="getSiteSummary()"
        ></pagination>
      </div>
    </div>

    <div v-else class="seo-keyword-page__panel">
      <div class="seo-keyword-page__panel-head">
        <div>
          <div class="seo-keyword-page__panel-title">该站点全部关键词</div>
          <div class="seo-keyword-page__panel-note">
            当前仅查看本地关键词与最近一次排名快照，不实时请求 DataForSEO
          </div>
        </div>
      </div>
      <div class="data-table-scroll" style="--table-min-width: 1610px">
        <el-table :data="pager.list" empty-text="暂无关键词数据">
          <el-table-column prop="id" label="ID" width="70"></el-table-column>
          <el-table-column
            prop="keyword"
            label="关键词"
            min-width="180"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column label="关键词类型" width="110">
            <template v-slot="scope">
              <el-tag size="small" type="info">
                {{ keywordTypeText(scope.row.keyword_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="target_domain"
            label="目标域名"
            min-width="150"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column label="来源" width="110">
            <template v-slot="scope">
              <el-tag
                size="small"
                :type="scope.row.provider === 'dataforseo' ? 'success' : 'info'"
              >
                {{ providerText(scope.row.provider) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="search_volume"
            label="搜索量"
            width="90"
          ></el-table-column>
          <el-table-column prop="cpc" label="CPC" width="90"></el-table-column>
          <el-table-column label="竞争度" width="120">
            <template v-slot="scope">
              <span>{{ scope.row.competition || "-" }}</span>
              <span v-if="scope.row.competition_level">
                / {{ scope.row.competition_level }}</span
              >
            </template>
          </el-table-column>
          <el-table-column label="参与排名" width="90">
            <template v-slot="scope">
              <el-tag size="small" v-if="scope.row.include_in_rank_check === 1"
                >参与</el-tag
              >
              <el-tag size="small" type="info" v-else>不参与</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="最新排名" width="90">
            <template v-slot="scope">
              <span v-if="scope.row.latest_rank_position">{{
                scope.row.latest_rank_position
              }}</span>
              <span v-else-if="scope.row.latest_checked_at">
                前{{ scope.row.latest_rank_depth || 50 }}未找到
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="达标" width="80">
            <template v-slot="scope">
              <el-tag
                size="small"
                :type="
                  Number(scope.row.is_qualified) === 1 ? 'success' : 'info'
                "
              >
                {{ Number(scope.row.is_qualified) === 1 ? "达标" : "未达标" }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="排名变化" width="90">
            <template v-slot="scope">
              <span v-if="scope.row.rank_change === null">-</span>
              <span
                v-else-if="scope.row.rank_change > 0"
                class="seo-keyword-page__rank-up"
                >+{{ scope.row.rank_change }}</span
              >
              <span
                v-else-if="scope.row.rank_change < 0"
                class="seo-keyword-page__rank-down"
                >{{ scope.row.rank_change }}</span
              >
              <span v-else>0</span>
            </template>
          </el-table-column>
          <el-table-column label="检查时间" width="160">
            <template v-slot="scope">
              <span v-if="scope.row.latest_checked_at">
                {{
                  formatDate(scope.row.latest_checked_at, "yyyy-mm-dd hh:MM:ss")
                }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="80">
            <template v-slot="scope">
              <el-tag size="small" v-if="scope.row.status === 1">正常</el-tag>
              <el-tag size="small" type="danger" v-else>停用</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="seo-keyword-page__pagination">
        <pagination
          v-model="pager"
          :pageSizes="[10, 20, 30]"
          @change="getList()"
        ></pagination>
      </div>
    </div>

    <keyword-dialog ref="keywordDialog" @refresh="onSaved"></keyword-dialog>
  </div>
</template>

<script>
import Pagination from "@/components/pagination";
import { Pager } from "@/utils/pager";
import { formatDate } from "@/utils/util";
import {
  apiSeoKeywordImport,
  apiSeoKeywordList,
  apiSeoKeywordSiteSummary,
  apiSeoSettingSyncRank,
  apiSeoSiteOptions,
} from "@/api/seo";
import KeywordDialog from "./keyword-dialog.vue";

export default {
  components: {
    KeywordDialog,
    Pagination,
  },
  data() {
    return {
      siteOptions: [],
      currentSite: {},
      importLoading: false,
      syncSiteLoadingId: 0,
      keywordTypeOptions: [
        { label: "主词", value: "core" },
        { label: "长尾词", value: "long_tail" },
        { label: "品牌词", value: "brand" },
        { label: "竞品词", value: "competitor" },
      ],
      siteParams: {
        site_id: "",
        site_url: "",
      },
      keywordParams: {
        site_id: "",
        keyword: "",
        target_domain: "",
        keyword_type: "",
        include_in_rank_check: "",
        qualified_status: "",
        status: "",
      },
      sitePager: new Pager(),
      pager: new Pager(),
    };
  },
  computed: {
    isKeywordDetail() {
      return Boolean(this.$route.params.siteId);
    },
    pageLoading() {
      return this.isKeywordDetail ? this.pager.loading : this.sitePager.loading;
    },
    pageTitle() {
      return this.isKeywordDetail ? "站点关键词" : "关键词管理";
    },
    pageDesc() {
      return this.isKeywordDetail
        ? "查看该站点下全部关键词、最新排名与达标状态"
        : "按站点维护关键词，进入站点后查看全部关键词";
    },
  },
  watch: {
    "$route.params.siteId": {
      immediate: false,
      handler() {
        this.bootstrap();
      },
    },
  },
  created() {
    this.bootstrap();
  },
  methods: {
    formatDate,
    bootstrap() {
      this.getSiteOptions();
      if (this.isKeywordDetail) {
        this.keywordParams.site_id = Number(this.$route.params.siteId || 0);
        this.loadCurrentSite();
        this.getList(1);
      } else {
        this.currentSite = {};
        this.getSiteSummary();
      }
    },
    getSiteOptions() {
      apiSeoSiteOptions().then((resp) => {
        this.siteOptions = resp || [];
      });
    },
    getSiteSummary(page) {
      page && (this.sitePager.page = page);
      this.sitePager.request({
        callback: apiSeoKeywordSiteSummary,
        params: { ...this.siteParams },
      });
    },
    loadCurrentSite() {
      const siteId = Number(this.$route.params.siteId || 0);
      if (!siteId) {
        this.backToSiteList();
        return;
      }
      apiSeoKeywordSiteSummary({ site_id: siteId, page: 1, size: 1 }).then(
        (resp) => {
          this.currentSite = (resp.list || [])[0] || { site_id: siteId };
        }
      );
    },
    getList(page) {
      page && (this.pager.page = page);
      this.keywordParams.site_id = Number(this.$route.params.siteId || 0);
      this.pager.request({
        callback: apiSeoKeywordList,
        params: { ...this.keywordParams },
      });
    },
    resetSiteFilters() {
      this.siteParams.site_id = "";
      this.siteParams.site_url = "";
      this.getSiteSummary(1);
    },
    resetKeywordFilters() {
      const siteId = Number(this.$route.params.siteId || 0);
      this.keywordParams = {
        site_id: siteId,
        keyword: "",
        target_domain: "",
        keyword_type: "",
        include_in_rank_check: "",
        qualified_status: "",
        status: "",
      };
      this.getList(1);
    },
    openSiteKeywords(row) {
      this.$router.push(`/seo/keyword/site/${row.site_id}`);
    },
    onSyncSiteRank(row) {
      this.$confirm(
        `将为站点“${row.site_name}”下所有启用且参与排名查询的关键词提交Standard异步任务，可能产生DataForSEO费用。每次手动同步都会新建一轮查询；上一轮结束后再次同步可能重复计费。月度自动查询仍按既定日期执行，也可能再次查询这些关键词。是否继续？`,
        "确认提交站点排名任务",
        { type: "warning" }
      ).then(() => {
        this.syncSiteLoadingId = row.site_id;
        apiSeoSettingSyncRank({ site_id: row.site_id })
          .then((resp) => {
            this.$message({
              type: "success",
              message: `已提交异步任务：处理${resp.processed || 0}个，提交${
                resp.submitted || 0
              }个，跳过${resp.skipped || 0}个，失败${
                resp.failed || 0
              }个。结果采集会由Standard采集任务继续处理。`,
            });
            this.getSiteSummary();
          })
          .finally(() => {
            this.syncSiteLoadingId = 0;
          });
      });
    },
    backToSiteList() {
      this.$router.push("/seo/keyword");
    },
    onNew() {
      this.$refs.keywordDialog.openDialog();
    },
    onImportClick() {
      this.$refs.importFileRefs && this.$refs.importFileRefs.click();
    },
    onImportFileChange(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      if (!/\.csv$/i.test(file.name)) {
        this.$message({ type: "error", message: "请上传CSV文件" });
        return;
      }
      this.importLoading = true;
      apiSeoKeywordImport(file)
        .then((resp) => {
          this.$message({
            type: "success",
            message: `导入成功，共${resp.import_count || 0}条`,
          });
          this.refreshCurrentView();
        })
        .finally(() => {
          this.importLoading = false;
        });
    },
    onDownloadTemplate() {
      const rows = [
        ["# SEO关键词CSV导入模板"],
        ["# 注释行以#开头，导入时会自动忽略。请保留表头行。"],
        ["# 站点ID：必填，填写后台站点管理中的站点ID，例如9。"],
        ["# 目标域名：必填，不带协议，例如czcdwl.com。"],
        ["# 目标URL：选填，可填写站点首页或目标页面完整URL。"],
        ["# 关键词：必填，每行一个关键词。"],
        ["# 设备：必填，仅支持desktop或mobile。"],
        ["# 语言代码：必填，DataForSEO语言代码，例如en。"],
        ["# 地区代码：选填，DataForSEO地区代码；美国常用2840。"],
        ["# 国家/地区：选填，用于展示，例如United States。"],
        [
          "# 关键词类型：必填，支持core/long_tail/brand/competitor，也支持中文主词/长尾词/品牌词/竞品词。",
        ],
        ["# 是否参与排名查询：必填，1=参与，0=不参与。"],
        [
          "站点ID",
          "目标域名",
          "目标URL",
          "关键词",
          "设备",
          "语言代码",
          "地区代码",
          "国家/地区",
          "关键词类型",
          "是否参与排名查询",
        ],
        [
          "9",
          "czcdwl.com",
          "https://www.czcdwl.com/",
          "Gearbox",
          "desktop",
          "en",
          "2840",
          "United States",
          "core",
          "1",
        ],
      ];
      const csv = rows
        .map((row) => row.map(this.escapeCsvValue).join(","))
        .join("\n");
      const blob = new Blob([`\uFEFF${csv}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "seo_keyword_import_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    escapeCsvValue(value) {
      const text = String(value);
      if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    },
    onSaved() {
      this.refreshCurrentView();
    },
    refreshCurrentView() {
      if (this.isKeywordDetail) {
        this.loadCurrentSite();
        this.getList();
      } else {
        this.getSiteSummary();
      }
    },
    providerText(provider) {
      return (
        { manual: "manual", dataforseo: "dataforseo" }[provider] ||
        provider ||
        "-"
      );
    },
    keywordTypeText(type) {
      return (
        {
          core: "主词",
          long_tail: "长尾词",
          brand: "品牌词",
          competitor: "竞品词",
        }[type] ||
        type ||
        "-"
      );
    },
    committedRankLimitText(value) {
      const limit = Number(value || 30);
      return limit === 10 ? "前10" : limit === 50 ? "前50" : "前30";
    },
  },
};
</script>

<style lang="scss" scoped>
.seo-keyword-page {
  min-height: calc(100vh - 84px);
  margin: -16px;
  padding: 24px;
  background: #f8fafc;
  color: #202124;

  &__header,
  &__panel,
  &__site-strip {
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 22px;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 30px;
  }

  &__desc {
    margin-top: 4px;
    color: #5f6368;
    font-size: 13px;
    line-height: 20px;
  }

  &__actions,
  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  &__site-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 14px;
    padding: 18px 20px;
  }

  &__site-title {
    color: #202124;
    font-size: 17px;
    font-weight: 700;
    line-height: 24px;
  }

  &__site-url {
    margin-top: 2px;
    color: #5f6368;
    font-size: 13px;
  }

  &__site-meta {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
  }

  &__site-metric {
    position: relative;
    min-width: 92px;
    padding: 10px 12px 10px 14px;
    overflow: hidden;
    background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
    border: 1px solid #dbe8ff;
    border-radius: 8px;

    &::before {
      position: absolute;
      top: 10px;
      bottom: 10px;
      left: 0;
      width: 3px;
      content: "";
      background: #1a73e8;
      border-radius: 0 3px 3px 0;
    }

    span {
      display: block;
      color: #5f6368;
      font-size: 12px;
      line-height: 16px;
    }

    strong {
      display: block;
      margin-top: 4px;
      color: #1a73e8;
      font-size: 18px;
      font-weight: 700;
      line-height: 22px;
      white-space: nowrap;
    }

    &--rate {
      min-width: 112px;

      &::before {
        background: #34a853;
      }

      strong {
        color: #188038;
      }
    }
  }

  &__panel {
    margin-top: 16px;
    padding: 18px;
  }

  &__filter-panel {
    padding: 14px 16px;
  }

  &__filter-site {
    width: 210px;
  }

  &__filter-input {
    width: 210px;
  }

  &__filter-small {
    width: 130px;
  }

  &__panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__panel-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__panel-note {
    margin-top: 2px;
    color: #80868b;
    font-size: 12px;
    line-height: 18px;
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  &__hidden-input {
    display: none;
  }

  &__rank-up {
    color: #34a853;
    font-weight: 600;
  }

  &__rank-down {
    color: #ea4335;
    font-weight: 600;
  }

  ::v-deep .el-table th.el-table__cell {
    background: #f8fafc;
    color: #5f6368;
    font-weight: 600;
  }
}

@media (max-width: 980px) {
  .seo-keyword-page {
    &__header,
    &__site-strip {
      flex-direction: column;
    }

    &__actions,
    &__filters {
      justify-content: flex-start;
    }
  }
}
</style>
